import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Mail, KeyRound, Loader2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useOnboardingAuth } from "../contexts/OnboardingAuthContext";

export default function OnboardingLoginPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const nextPath = search.get("next") || "/skapa-ai-agent";
  const { requestCode, verifyCode } = useOnboardingAuth();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleSendCode = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes("@")) {
      toast.error("Skriv in en giltig e-postadress");
      return;
    }
    setSending(true);
    try {
      await requestCode(trimmed);
      toast.success("Kod skickad — kolla din inkorg");
      setStep("code");
    } catch (err) {
      toast.error(err.message || "Kunde inte skicka kod");
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (e) => {
    e?.preventDefault?.();
    const c = code.trim();
    if (!/^\d{6}$/.test(c)) {
      toast.error("Koden är 6 siffror");
      return;
    }
    setVerifying(true);
    try {
      await verifyCode(email.trim().toLowerCase(), c);
      toast.success("Inloggad!");
      navigate(nextPath);
    } catch (err) {
      toast.error(err.message || "Fel kod");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <main
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F2A30] via-[#15414A] to-[#0F2A30] text-white px-4 py-12 relative overflow-hidden"
      data-testid="onboarding-login-page"
    >
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="w-full max-w-md relative">
        <Link
          to="/skapa-ai-agent"
          className="text-xs text-white/50 hover:text-white inline-flex items-center gap-1 mb-6"
          data-testid="onboarding-back-link"
        >
          ← Tillbaka
        </Link>
        <div className="bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-2xl p-7 sm:p-9">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#7FD4DE] mb-2">
            Elido onboarding
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight">
            {step === "email" ? "Logga in för att skapa utkast" : "Skriv in koden"}
          </h1>
          <p className="text-sm text-white/60 mt-2 leading-relaxed">
            {step === "email"
              ? "Vi mejlar en 6-siffrig kod. Snabbt och utan lösenord."
              : `Vi mejlade en kod till ${email}. Koden är giltig i 15 minuter.`}
          </p>

          {step === "email" ? (
            <form onSubmit={handleSendCode} className="mt-7 space-y-5">
              <div>
                <Label htmlFor="ob-email" className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/60">
                  E-postadress
                </Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                  <Input
                    id="ob-email"
                    type="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="namn@företag.se"
                    className="pl-11 h-12 bg-[#0a1f24] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#7FD4DE]/50 focus-visible:border-[#7FD4DE]/60 rounded-xl"
                    data-testid="onboarding-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="w-full bg-gradient-to-r from-[#3A8A98] to-[#7FD4DE] hover:from-[#2c7280] hover:to-[#6cc4cf] text-[#0F2A30] font-semibold rounded-xl"
                data-testid="onboarding-send-code-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                Skicka kod
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="mt-7 space-y-5">
              <div>
                <Label htmlFor="ob-code" className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/60">
                  6-siffrig kod
                </Label>
                <div className="relative mt-2">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                  <Input
                    id="ob-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={6}
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    className="pl-11 h-12 tracking-[0.5em] font-mono text-lg bg-[#0a1f24] border-white/10 text-white placeholder:text-white/20 focus-visible:ring-[#7FD4DE]/50 focus-visible:border-[#7FD4DE]/60 rounded-xl"
                    data-testid="onboarding-code-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={verifying || code.length !== 6}
                className="w-full bg-gradient-to-r from-[#3A8A98] to-[#7FD4DE] hover:from-[#2c7280] hover:to-[#6cc4cf] text-[#0F2A30] font-semibold rounded-xl"
                data-testid="onboarding-verify-code-btn"
              >
                {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Verifiera och logga in
              </Button>
              <button
                type="button"
                onClick={() => { setStep("email"); setCode(""); }}
                className="text-xs text-white/50 hover:text-white w-full text-center transition-colors"
                data-testid="onboarding-back-to-email-btn"
              >
                Byta e-post
              </button>
            </form>
          )}
        </div>
        <p className="text-[11px] text-white/40 mt-6 text-center leading-relaxed">
          Genom att fortsätta godkänner du Elidos integritetspolicy.
          Vi använder din e-post bara för att skicka inloggningslänken och kontakta dig om utkastet.
        </p>
      </div>
    </main>
  );
}
